import computeShotXg, { computePreShotXg } from './xgModel.js';

const NAME_CANDIDATE_KEYS = ['nick', 'player', 'name', 'keeper', 'goalie', 'by', 'saver', 'author', 'scorer'];

const ON_TARGET_OUTCOMES = new Set(['goal', 'saved', 'block', 'blocked', 'deflect', 'deflected', 'rebound', 'tip', 'post-save', 'parried']);

function normalizeTeam(value) {
  if (!value && value !== 0) return null;
  const str = String(value).trim().toLowerCase();
  if (str === 'red') return 'red';
  if (str === 'blue') return 'blue';
  return null;
}

function teamAttacksPositive(team) {
  return normalizeTeam(team) === 'red';
}

function getFinalThirdBoundary(match) {
  const width = Number(match?.stadium?.width) || 800;
  return width * 0.25;
}

const OFF_TARGET_OUTCOMES = new Set(['offtarget', 'off-target', 'off_target', 'wide', 'miss', 'missed', 'out', 'off', 'post', 'bar', 'woodwork', 'abandoned']);

const DANGEROUS_GOAL_TICK_THRESHOLD = 720; // 12 seconds

export const toFiniteNumber = (value) => {
  if (value === undefined || value === null) return null;
  const num = typeof value === 'string' ? parseFloat(value) : Number(value);
  return Number.isFinite(num) ? num : null;
};

export function resolvePlayerName(value) {
  if (value === undefined || value === null) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }
  if (typeof value === 'object') {
    for (const key of NAME_CANDIDATE_KEYS) {
      const candidate = value[key];
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate.trim();
      }
    }
    if (typeof value.display === 'string' && value.display.trim()) {
      return value.display.trim();
    }
    if (Array.isArray(value)) {
      for (const item of value) {
        const resolved = resolvePlayerName(item);
        if (resolved) return resolved;
      }
    }
  }
  return null;
}

function pickField(obj, keys) {
  if (!obj || typeof obj !== 'object') return null;
  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null) return obj[key];
  }
  return null;
}

function firstFiniteNumber(values) {
  for (const value of values) {
    const num = toFiniteNumber(value);
    if (num !== null) return num;
  }
  return null;
}

export function extractShotXg(shot) {
  if (!shot || typeof shot !== 'object') return 0;
  const candidate = firstFiniteNumber([
    shot.xg,
    shot.expectedGoals,
    shot.expected_goal,
    pickField(shot.model, ['xg', 'expectedGoals', 'expected_goal']),
    pickField(shot.metrics, ['xg', 'expectedGoals']),
    pickField(shot.analysis, ['xg']),
    pickField(shot.stats, ['xg']),
    pickField(shot.result, ['xg', 'expectedGoals']),
    pickField(shot.probabilities, ['xg']),
    pickField(shot.model?.result, ['xg', 'expectedGoals'])
  ]);
  if (candidate === null) return 0;
  return Math.max(0, candidate);
}

export function isShotOnTarget(shot) {
  if (!shot || typeof shot !== 'object') return false;
  const outcomeRaw = shot.outcome || pickField(shot.result, ['outcome', 'type']);
  if (outcomeRaw) {
    const outcome = String(outcomeRaw).toLowerCase();
    if (ON_TARGET_OUTCOMES.has(outcome)) return true;
    if (OFF_TARGET_OUTCOMES.has(outcome)) return false;
  }
  const explicit = pickField(shot, ['onTarget', 'on_target', 'onGoal', 'on_goal', 'onFrame']);
  if (explicit !== null) return explicit !== false && explicit !== 0;
  const modelExplicit = pickField(shot.model, ['onTarget', 'on_target', 'onGoal']);
  if (modelExplicit !== null) return modelExplicit !== false && modelExplicit !== 0;
  if (shot.goal === true || shot.scored === true) return true;
  return false;
}

export function extractSavedBy(shot) {
  if (!shot || typeof shot !== 'object') return null;
  const candidates = [
    shot.savedBy,
    shot.saved_by,
    shot.save?.by,
    shot.save?.player,
    shot.save?.keeper,
    shot.save?.nick,
    shot.result?.savedBy,
    shot.result?.saved_by,
    shot.result?.by,
    shot.model?.savedBy,
    shot.model?.saved_by,
    shot.model?.save?.by,
    shot.goalkeeper,
    shot.goalkeeperName,
    shot.blockedBy,
    shot.blocked_by,
    shot.defender,
    shot.stopper
  ];
  for (const candidate of candidates) {
    const name = resolvePlayerName(candidate);
    if (name) return name;
  }
  return null;
}

function inferShotTeam(shot, options = {}) {
  if (shot && typeof shot.team === 'string') {
    const team = String(shot.team);
    if (team === 'Red' || team === 'Blue') return team;
  }
  const shooter = resolvePlayerName(shot?.player ?? shot?.shooter);
  if (shooter) {
    try {
      if (Array.isArray(options.redTeam) && options.redTeam.includes(shooter)) return 'Red';
      if (Array.isArray(options.blueTeam) && options.blueTeam.includes(shooter)) return 'Blue';
    } catch (_) {}
  }
  const goalX = toFiniteNumber(shot?.to?.x);
  if (goalX !== null) {
    if (goalX > 0) return 'Red';
    if (goalX < 0) return 'Blue';
  }
  return null;
}

export function estimateShotXg(shot, options = {}) {
  try {
    const team = inferShotTeam(shot, options);
    const fromX = toFiniteNumber(shot?.from?.x) ?? 0;
    const fromY = toFiniteNumber(shot?.from?.y) ?? 0;
    const toX = toFiniteNumber(shot?.to?.x);
    const toY = toFiniteNumber(shot?.to?.y);
    const defaultGoalHeight = 130;
    // `to` is the impact point on (or beyond) the goal plane.  It is not the
    // centre of the goal mouth: using `to.y` as the centre makes a shot aimed
    // at the post look like a central shot and changes the chance estimate
    // using information that is only known after the kick.  Prefer explicit
    // goal geometry and fall back to the legacy target x coordinate only when
    // no geometry was captured.
    const targetTeam = normalizeTeam(shot?.targetGoal ?? shot?.targetTeam ?? shot?.target_team);
    const stadiumGoal = Array.isArray(options.stadium?.goals)
      ? options.stadium.goals.find(goalEntry => normalizeTeam(goalEntry?.team) === targetTeam)
      : null;
    const goalPoints = stadiumGoal?.p0 && stadiumGoal?.p1
      ? { x: firstFiniteNumber([stadiumGoal.p0[0], stadiumGoal.p1[0]]), yTop: firstFiniteNumber([stadiumGoal.p0[1], stadiumGoal.p1[1]]), yBottom: firstFiniteNumber([stadiumGoal.p1[1], stadiumGoal.p0[1]]) }
      : null;
    const goalGeometry = shot?.goal || options.goal || goalPoints || null;
    const goalHeight = Number.isFinite(toFiniteNumber(goalGeometry?.yTop))
      && Number.isFinite(toFiniteNumber(goalGeometry?.yBottom))
      ? Math.abs(toFiniteNumber(goalGeometry.yBottom) - toFiniteNumber(goalGeometry.yTop))
      : Number.isFinite(options.goalHeight) ? options.goalHeight : defaultGoalHeight;
    const explicitGoalX = firstFiniteNumber([
      shot?.goalX,
      shot?.goal_x,
      shot?.targetGoalX,
      shot?.target_goal_x,
      goalGeometry?.x,
      options.goalX,
      options.goal_x
    ]);
    const goalX = explicitGoalX !== null
      ? explicitGoalX
      : Number.isFinite(toX)
        ? toX
        : team === 'Red'
          ? 420
          : team === 'Blue'
            ? -420
            : 420;
    const explicitGoalCenterY = firstFiniteNumber([
      shot?.goalCenterY,
      shot?.goal_center_y,
      shot?.goalY,
      shot?.goal_y,
      goalGeometry?.centerY,
      goalGeometry?.y,
      options.goalCenterY,
      options.goal_center_y,
      options.goalY,
      options.goal_y
    ]);
    const goalCenterY = explicitGoalCenterY !== null
      ? explicitGoalCenterY
      : Number.isFinite(toFiniteNumber(goalGeometry?.yTop)) && Number.isFinite(toFiniteNumber(goalGeometry?.yBottom))
        ? (toFiniteNumber(goalGeometry.yTop) + toFiniteNumber(goalGeometry.yBottom)) / 2
        : 0;
    const goal = {
      x: goalX,
      yTop: goalCenterY - goalHeight / 2,
      yBottom: goalCenterY + goalHeight / 2
    };
    const rawVx = toFiniteNumber(shot?.ball?.vx);
    const rawVy = toFiniteNumber(shot?.ball?.vy);
    const vx = Number.isFinite(rawVx)
      ? rawVx
      : Number.isFinite(toX) ? (toX - fromX) : 0;
    const vy = Number.isFinite(rawVy)
      ? rawVy
      : Number.isFinite(toY) ? (toY - fromY) : 0;
    const ball = {
      x: fromX,
      y: fromY,
      vx: vx ?? 0,
      vy: vy ?? 0
    };
    const context = {
      goal,
      ball,
      stadium: options.stadium || null,
      shooter: {
        position: { x: fromX, y: fromY },
        velocity: { x: vx ?? 0, y: vy ?? 0 }
      },
      // Outcome flags are optional.  Do not infer them from `outcome` or
      // default to on-target: doing so leaks the observed shot result into a
      // pre-shot xG estimate.  When the extractor has an explicit flag, keep
      // the historical outcome adjustment for compatibility with old reports.
      ...(shot?.onTarget === true || shot?.on_target === true
        ? { isOnTarget: true }
        : shot?.offTarget === true || shot?.off_target === true
          ? { isOffTarget: true }
          : {})
    };
    const result = options.preShot === true
      ? computePreShotXg(context, { calibration: options.xgCalibration })
      : computeShotXg(context);
    const xg = Number(result.xg);
    return Number.isFinite(xg) ? Math.max(0, xg) : 0.05;
  } catch (_) {
    return 0.05;
  }
}

/**
 * Estimate opportunity quality from the pre-kick state. This explicit name
 * prevents callers from accidentally using the legacy outcome-adjusted path
 * for a probability that is meant to describe the chance before execution.
 */
export function estimatePreShotXg(shot, options = {}) {
  return estimateShotXg(shot, { ...options, preShot: true });
}

export function summarizeShots(shots, options = {}) {
  const byShooter = new Map();
  const savedBy = new Map();
  if (!Array.isArray(shots)) return { byShooter, savedBy };
  shots.forEach((shot) => {
    const shooter = resolvePlayerName(shot?.player ?? shot?.shooter ?? shot?.author);
    if (!shooter) return;
    const entry = byShooter.get(shooter) || { total: 0, onTarget: 0, offTarget: 0, xg: 0, xgOnTarget: 0 };
    if (!Number.isFinite(entry.offTarget)) entry.offTarget = entry.offTarget || 0;
    entry.total += 1;
    const onTarget = isShotOnTarget(shot);
    if (onTarget) {
      entry.onTarget += 1;
    } else {
      entry.offTarget = (entry.offTarget || 0) + 1;
    }
    let xg = extractShotXg(shot);
    if (!Number.isFinite(xg) || xg <= 0) {
      xg = estimateShotXg(shot, options);
    }
    if (Number.isFinite(xg)) {
      entry.xg += xg;
      if (onTarget) entry.xgOnTarget += xg;
    }
    byShooter.set(shooter, entry);
    const keeper = extractSavedBy(shot);
    if (keeper) {
      savedBy.set(keeper, (savedBy.get(keeper) || 0) + 1);
    }
  });
  return { byShooter, savedBy };
}

export function summarizeGoals(goals) {
  const goalMap = new Map();
  const assistMap = new Map();
  if (!Array.isArray(goals)) return { goals: goalMap, assists: assistMap };
  goals.forEach((goal) => {
    const scorer = resolvePlayerName(goal?.scorer ?? goal?.player ?? goal?.author ?? goal?.nick);
    if (scorer) goalMap.set(scorer, (goalMap.get(scorer) || 0) + 1);
    const assistCandidate = goal?.assist ?? goal?.assistPlayer ?? goal?.assist_player ?? goal?.assist?.player ?? goal?.assist?.nick ?? goal?.assistName ?? goal?.assist_name;
    const assist = resolvePlayerName(assistCandidate);
    if (assist) assistMap.set(assist, (assistMap.get(assist) || 0) + 1);
  });
  return { goals: goalMap, assists: assistMap };
}

export function summarizeKicks(kicks) {
  const map = new Map();
  if (!Array.isArray(kicks)) return map;
  kicks.forEach((kick) => {
    const name = resolvePlayerName(kick?.player ?? kick?.nick ?? kick);
    if (name) map.set(name, (map.get(name) || 0) + 1);
  });
  return map;
}

export function summarizePasses(passes) {
  const map = new Map();
  if (!Array.isArray(passes)) return map;
  passes.forEach((pass) => {
    const name = resolvePlayerName(
      typeof pass === 'string'
        ? pass
        : pass?.player ?? pass?.passer ?? pass?.author ?? pass?.from ?? pass?.source
    );
    if (name) map.set(name, (map.get(name) || 0) + 1);
  });
  return map;
}

export function summarizeDirectionalPasses(passes) {
  const map = new Map();
  if (!Array.isArray(passes)) return map;
  passes.forEach((pass) => {
    if (!pass || typeof pass !== 'object') return;
    const direction = typeof pass.direction === 'string' ? pass.direction.toLowerCase() : null;
    if (direction !== 'forward' && direction !== 'lateral' && direction !== 'backward') return;
    const name = resolvePlayerName(pass.player ?? pass.passer ?? pass.author ?? pass.from ?? pass.source);
    if (!name) return;
    const entry = map.get(name) || { forward: 0, lateral: 0, backward: 0 };
    if (direction === 'forward') entry.forward += 1;
    else if (direction === 'lateral') entry.lateral += 1;
    else entry.backward += 1;
    map.set(name, entry);
  });
  return map;
}

export function summarizeSimpleList(list) {
  const map = new Map();
  if (!Array.isArray(list)) return map;
  list.forEach((entry) => {
    const name = resolvePlayerName(entry?.player ?? entry?.nick ?? entry);
    if (name) map.set(name, (map.get(name) || 0) + 1);
  });
  return map;
}

export function summarizeEvents(list, keyMapper) {
  const map = new Map();
  if (!Array.isArray(list)) return map;
  list.forEach((entry) => {
    if (!entry || typeof entry !== 'object') return;
    const name = resolvePlayerName(entry.player ?? entry.nick ?? entry.by);
    if (!name) return;
    const bucketKey = typeof keyMapper === 'function' ? keyMapper(entry) : 'total';
    const current = map.get(name) || { total: 0 };
    current.total += 1;
    if (bucketKey && bucketKey !== 'total') {
      current[bucketKey] = (current[bucketKey] || 0) + 1;
    }
    map.set(name, current);
  });
  return map;
}

export function summarizeSaves(saves, savedByFromShots) {
  const map = new Map();
  if (Array.isArray(saves)) {
    saves.forEach((save) => {
      const keeper = resolvePlayerName(save?.player ?? save?.keeper ?? save?.nick ?? save);
      if (keeper) map.set(keeper, (map.get(keeper) || 0) + 1);
    });
  }
  if (savedByFromShots instanceof Map) {
    savedByFromShots.forEach((count, keeper) => {
      const existing = map.has(keeper) ? map.get(keeper) : 0;
      map.set(keeper, Math.max(existing, count));
    });
  }
  return map;
}

export function summarizeDuels(duels) {
  const map = new Map();
  if (!Array.isArray(duels)) return map;
  duels.forEach((entry) => {
    if (!entry || typeof entry !== 'object') return;
    const winnerName = resolvePlayerName(entry.player);
    if (winnerName) {
      const record = map.get(winnerName) || { total: 0, corner: 0, interception: 0, wins: 0, losses: 0 };
      record.total += 1;
      if (entry.type === 'corner') record.corner += 1;
      else if (entry.type === 'interception') record.interception += 1;
      record.wins += 1;
      map.set(winnerName, record);
    }
    const loserName = resolvePlayerName(entry.loser);
    if (loserName) {
      const record = map.get(loserName) || { total: 0, corner: 0, interception: 0, wins: 0, losses: 0 };
      record.total += 1;
      record.losses += 1;
      map.set(loserName, record);
    }
  });
  return map;
}

export function summarizeRebounds(rebounds) {
  const map = new Map();
  if (!Array.isArray(rebounds)) return map;
  rebounds.forEach((entry) => {
    if (!entry || typeof entry !== 'object') return;
    const name = resolvePlayerName(entry.player ?? entry.nick);
    if (!name) return;
    const record = map.get(name) || { total: 0, clearanceRecoveries: 0 };
    record.total += 1;
    if (entry.recoveredClearance) record.clearanceRecoveries += 1;
    map.set(name, record);
  });
  return map;
}

export function summarizeClearances(clearances) {
  return summarizeEvents(clearances);
}

export function summarizeClearanceRecoveries(clearanceRecoveries) {
  return summarizeEvents(clearanceRecoveries);
}

export function summarizeGoalkeeperTicks(gkList) {
  const map = new Map();
  if (!Array.isArray(gkList)) return map;
  gkList.forEach((entry) => {
    if (!entry || typeof entry !== 'object') return;
    const name = resolvePlayerName(entry.name ?? entry.player ?? entry.nick);
    if (!name) return;
    const ticks = Number(entry.ticks) || 0;
    if (ticks <= 0) return;
    const team = typeof entry.team === 'string' ? entry.team : null;
    const existing = map.get(name) || { ticks: 0, team: team || null };
    existing.ticks += ticks;
    if (!existing.team && team) existing.team = team;
    map.set(name, existing);
  });
  return map;
}

export function extractAttemptedPasses(player, fallback = 0) {
  if (!player || typeof player !== 'object') return fallback;
  const num = firstFiniteNumber([
    player.attemptedPasses,
    player.attempted_passes,
    player.passAttempts,
    player.pass_attempts,
    player.passesAttempted,
    player.passes_attempted,
    player.passes?.attempted,
    player.passes?.attempts,
    player.passing?.attempted,
    player.passing?.attempts,
    player.stats?.passes?.attempted,
    player.stats?.passes_attempted,
    player.metrics?.passesAttempted,
    player.metrics?.passes_attempted
  ]);
  if (num === null) return fallback;
  return Math.max(0, num);
}

export function extractCompletedPasses(player, fallback = 0) {
  if (!player || typeof player !== 'object') return fallback;
  const num = firstFiniteNumber([
    player.successfulPasses,
    player.successful_passes,
    player.passesCompleted,
    player.passes_completed,
    player.passes?.completed,
    player.passing?.completed,
    player.passing?.success,
    player.stats?.passes?.completed,
    player.stats?.passes_completed,
    player.metrics?.passesCompleted,
    player.metrics?.passes_completed
  ]);
  if (num === null) return fallback;
  return Math.max(0, num);
}

export function extractTurnoverTouches(source, fallback = 0) {
  if (!source || typeof source !== 'object') return fallback;
  const num = firstFiniteNumber([
    source.turnoverTouches,
    source.turnover_touches,
    source.turnoverTouchCount,
    source.turnover_touch_count,
    source.turnoversCommitted,
    source.turnovers_committed,
    source.turnovers,
    source.metrics?.turnoverTouches,
    source.metrics?.turnover_touches,
    source.metrics?.turnoverTouchCount,
    source.metrics?.turnoversCommitted,
    source.metrics?.turnovers_committed,
    source.metrics?.turnovers,
    source.stats?.turnoverTouches,
    source.stats?.turnover_touches,
    source.stats?.turnoversCommitted,
    source.stats?.turnovers_committed,
    source.advanced?.turnoverTouches,
    source.advanced?.turnover_touches,
    source.advanced?.turnoversCommitted,
    source.advanced?.turnovers_committed,
    source.summary?.turnoverTouches,
    source.summary?.turnoversCommitted
  ]);
  if (num === null) return fallback;
  return Math.max(0, num);
}

export function extractDangerousTurnovers(source, fallback = 0) {
  if (!source || typeof source !== 'object') return fallback;
  const num = firstFiniteNumber([
    source.dangerousTurnovers,
    source.dangerous_turnovers,
    source.dangerTurnovers,
    source.danger_turnovers,
    source.metrics?.dangerousTurnovers,
    source.metrics?.dangerous_turnovers,
    source.metrics?.dangerTurnovers,
    source.stats?.dangerousTurnovers,
    source.stats?.dangerous_turnovers,
    source.advanced?.dangerousTurnovers,
    source.advanced?.dangerous_turnovers,
    source.summary?.dangerousTurnovers
  ]);
  if (num === null) return fallback;
  return Math.max(0, num);
}

export function extractPlayerXg(player, fallback = 0) {
  if (!player || typeof player !== 'object') return fallback;
  const num = firstFiniteNumber([
    player.xg,
    player.expectedGoals,
    player.expected_goals,
    player.metrics?.xg,
    player.metrics?.expectedGoals,
    player.metrics?.expected_goals,
    player.stats?.xg,
    player.stats?.expectedGoals,
    player.offense?.xg,
    player.offense?.expectedGoals,
    player.shooting?.xg,
    player.shotStats?.xg,
    player.advanced?.xg
  ]);
  if (num === null) return fallback;
  return Math.max(0, num);
}

export function getCount(map, key) {
  if (!map || typeof map.get !== 'function') return 0;
  return map.get(key) || 0;
}

const POSSESSION_GAP_TICKS = 240;
const GOAL_LOOKAHEAD_TICKS = 240;

function createPossessionEntry({ team, startTick, startPlayer, startReplayTick = null }) {
  return {
    team,
    startTick,
    startReplayTick: toFiniteNumber(startReplayTick),
    endTick: startTick,
    endReplayTick: toFiniteNumber(startReplayTick),
    firstPlayer: startPlayer || null,
    lastPlayer: startPlayer || null,
    lastTick: startTick,
    lastReplayTick: toFiniteNumber(startReplayTick),
    players: new Map(),
    playerSequence: [],
    ticks: [],
    replayTicks: []
  };
}

function registerPossessionTouch(possession, playerName, tick, pressured, replayTick = null) {
  if (!playerName) return;
  const entry = possession.players.get(playerName) || { touches: 0, pressured: 0, ticks: [], replayTicks: [] };
  entry.touches += 1;
  if (pressured) entry.pressured += 1;
  entry.ticks.push(tick);
  if (Number.isFinite(replayTick)) entry.replayTicks.push(replayTick);
  possession.players.set(playerName, entry);
  if (possession.playerSequence.at(-1) !== playerName) possession.playerSequence.push(playerName);
  possession.lastPlayer = playerName;
}


function countTeamPassesInWindow(passEvents, team, startTick, endTick) {
  if (!team || !Array.isArray(passEvents)) return 0;
  const start = Number(startTick);
  const end = Number(endTick);
  return passEvents.reduce((count, pass) => {
    if (!pass || typeof pass !== 'object') return count;
    const passTeam = normalizeTeam(pass?.team ?? pass?.lastShot?.team ?? pass?.shot?.team);
    if (passTeam !== team) return count;
    const tick = Number(pass?.lastShot?.tick ?? pass?.matchTick ?? pass?.gameTicks ?? pass?.tick);
    if (!Number.isFinite(tick)) return count;
    if (Number.isFinite(start) && tick < start) return count;
    if (Number.isFinite(end) && tick > end) return count;
    return count + 1;
  }, 0);
}

export function buildPossessionTimeline(match) {
  const kicks = Array.isArray(match?.kicks) ? [...match.kicks] : [];
  const goals = Array.isArray(match?.goals) ? match.goals : [];
  kicks.sort((a, b) => (Number(a?.tick) || 0) - (Number(b?.tick) || 0));

  const goalTimeline = goals
    .map((goal) => {
      const tick = Number(goal?.gameTicks ?? goal?.shot?.tick ?? goal?.clockTicks ?? goal?.replayTick);
      const team = goal?.for ?? goal?.team ?? goal?.shot?.team ?? goal?.scorerTeam;
      return {
        tick: Number.isFinite(tick) ? tick : null,
        team: normalizeTeam(team),
        scorer: resolvePlayerName(goal?.scorer)
      };
    })
    .filter((item) => item.tick !== null && item.team)
    .sort((a, b) => a.tick - b.tick);

  let goalPtr = 0;
  const possessions = [];
  let current = null;

  const finalizeCurrent = (nextStartTick, nextTeam) => {
    if (!current) return;
    const windowEnd = nextStartTick !== null && nextStartTick !== undefined
      ? nextStartTick
      : current.endTick + GOAL_LOOKAHEAD_TICKS;
    let endedWithGoal = false;
    while (goalPtr < goalTimeline.length) {
      const goal = goalTimeline[goalPtr];
      if (goal.tick < current.startTick) {
        goalPtr += 1;
        continue;
      }
      if (goal.tick <= windowEnd && goal.team === current.team) {
        endedWithGoal = true;
        current.result = 'goal';
        current.goalTick = goal.tick;
        current.goalScorer = goal.scorer || null;
        goalPtr += 1;
        break;
      }
      break;
    }
    if (!endedWithGoal) {
      current.result = nextTeam && nextTeam !== current.team ? 'turnover' : 'retained';
    }
    possessions.push(current);
    current = null;
  };

  kicks.forEach((kick, index) => {
    const tick = Number(kick?.tick);
    const replayTick = toFiniteNumber(kick?.replayTick);
    const team = normalizeTeam(kick?.team);
    const player = resolvePlayerName(kick?.player ?? kick?.nick);
    const pressured = !!kick?.pressured;
    if (!Number.isFinite(tick) || !team) {
      return;
    }
    const nextKick = kicks[index + 1];
    const nextTick = Number(nextKick?.tick);
    const shouldSplit =
      !current ||
      current.team !== team ||
      (Number.isFinite(current.lastTick) && tick - current.lastTick > POSSESSION_GAP_TICKS);
    if (shouldSplit) {
      finalizeCurrent(tick, team);
      current = createPossessionEntry({ team, startTick: tick, startPlayer: player, startReplayTick: replayTick });
    }
    current.lastTick = tick;
    current.endTick = tick;
    if (Number.isFinite(replayTick)) {
      current.lastReplayTick = replayTick;
      current.endReplayTick = replayTick;
      current.replayTicks.push(replayTick);
    }
    current.ticks.push(tick);
    registerPossessionTouch(current, player, tick, pressured, replayTick);
    if (!current.firstPlayer && player) current.firstPlayer = player;
    if (!Number.isFinite(nextTick) || nextTick - tick > POSSESSION_GAP_TICKS) {
      finalizeCurrent(nextTick, nextKick ? normalizeTeam(nextKick.team) : null);
    }
  });
  finalizeCurrent(null, null);

  return possessions;
}

function summarizePossessionContributions(possessions, match) {
  const passEvents = Array.isArray(match?.passes) ? match.passes : [];
  const map = new Map();
  const ensureEntry = (nick) => {
    if (!nick) return null;
    const existing = map.get(nick);
    if (existing) return existing;
    const created = {
      touches: 0,
      pressuredTouches: 0,
      possessions: 0,
      possessionStarts: 0,
      scoringTouches: 0,
      turnoverTouches: 0,
      dangerousTurnovers: 0,
      turnoversCommitted: 0,
      forcedTurnovers: 0,
      touchesLeadingEvents: 0
    };
    map.set(nick, created);
    return created;
  };

  possessions.forEach((possession, idx) => {
    const next = possessions[idx + 1];
    const endedByTurnover = possession.result === 'turnover';
    const opponentTeam = endedByTurnover && next && next.team && next.team !== possession.team ? next.team : null;
    const opponentPasses = opponentTeam ? countTeamPassesInWindow(passEvents, opponentTeam, next.startTick, next.endTick) : 0;
    const goalTick = next ? Number(next.goalTick ?? next.endTick ?? next.startTick) : NaN;
    const possessionEnd = Number(possession.endTick);
    const timeToGoal = Number.isFinite(goalTick) && Number.isFinite(possessionEnd) ? goalTick - possessionEnd : null;
    const dangerousTurnover = endedByTurnover && opponentTeam && next && next.result === 'goal' && (timeToGoal === null || timeToGoal <= DANGEROUS_GOAL_TICK_THRESHOLD);
    const turnoverPlayer = endedByTurnover && opponentPasses >= 2 ? possession.lastPlayer : null;
    const turnoverPlayerInfo = turnoverPlayer ? possession.players.get(turnoverPlayer) : null;
    const turnoverTouchCount = turnoverPlayerInfo ? turnoverPlayerInfo.touches : 0;
    let forcedTurnoverCandidate = null;
    if (endedByTurnover && next && next.team && next.team !== possession.team) {
      const nextPlayers = next.players instanceof Map ? next.players : null;
      if (nextPlayers && nextPlayers.size >= 2 && next.firstPlayer) {
        forcedTurnoverCandidate = next.firstPlayer;
      }
    }

    possession.players.forEach((info, nick) => {
      const entry = ensureEntry(nick);
      entry.touches += info.touches;
      entry.pressuredTouches += info.pressured;
      entry.possessions += 1;
      if (nick === possession.firstPlayer) entry.possessionStarts += 1;
      if (possession.result === 'goal') entry.scoringTouches += info.touches;
      if (turnoverPlayer && nick === turnoverPlayer) {
        entry.turnoverTouches += turnoverTouchCount || 0;
      }
      if (dangerousTurnover && nick === possession.lastPlayer) entry.dangerousTurnovers += 1;
      if (endedByTurnover && nick === possession.lastPlayer) entry.turnoversCommitted += 1;
    });

    if (forcedTurnoverCandidate) {
      const entry = ensureEntry(forcedTurnoverCandidate);
      if (entry) entry.forcedTurnovers = (entry.forcedTurnovers || 0) + 1;
    }
  });
  return map;
}

function summarizePassProgressionAdvanced(passes, match) {
  const map = new Map();
  const boundary = getFinalThirdBoundary(match);
  const fieldWidth = Number(match?.stadium?.width) || 800;
  const halfWidth = fieldWidth / 2;
  const progressiveThreshold = Math.max(40, fieldWidth * 0.05);
  (Array.isArray(passes) ? passes : []).forEach((pass) => {
    if (!pass || typeof pass !== 'object') return;
    const name = resolvePlayerName(pass.player ?? pass.passer ?? pass.author ?? pass.from ?? pass.source);
    if (!name) return;
    const team = normalizeTeam(pass?.lastShot?.team ?? pass?.team);
    const fromX = Number(pass?.lastShot?.x);
    const fromY = Number(pass?.lastShot?.y);
    const toX = Number(pass?.shot?.x);
    const toY = Number(pass?.shot?.y);
    if (!Number.isFinite(fromX) || !Number.isFinite(toX) || !team) return;
    const deltaX = toX - fromX;
    const deltaY = Number.isFinite(toY) && Number.isFinite(fromY) ? toY - fromY : 0;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    const direction = typeof pass.direction === 'string' ? pass.direction.toLowerCase() : null;
    const attackingPositive = teamAttacksPositive(team);
    const forwardMovement = attackingPositive ? deltaX : -deltaX;
    const progressive = forwardMovement > progressiveThreshold;
    const entry = map.get(name) || {
      progressivePasses: 0,
      progressiveDistance: 0,
      finalThirdEntries: 0,
      keyPasses: 0,
      backwardPasses: 0,
      lateralPasses: 0,
      forwardPasses: 0
    };
    if (direction === 'forward') entry.forwardPasses += 1;
    else if (direction === 'backward') entry.backwardPasses += 1;
    else if (direction === 'lateral') entry.lateralPasses += 1;
    if (progressive) {
      entry.progressivePasses += 1;
      entry.progressiveDistance += forwardMovement;
    }
    if (team === 'red' && toX >= boundary) entry.finalThirdEntries += 1;
    if (team === 'blue' && toX <= -boundary) entry.finalThirdEntries += 1;
    if (distance > halfWidth * 0.4 && forwardMovement > 0) entry.keyPasses += 1;
    map.set(name, entry);
  });
  return map;
}

function summarizeDuelsForPressures(duels) {
  const map = new Map();
  (Array.isArray(duels) ? duels : []).forEach((duel) => {
    if (!duel || typeof duel !== 'object') return;
    const winner = resolvePlayerName(duel.player);
    const loser = resolvePlayerName(duel.loser ?? duel.opponent);
    const result = typeof duel.result === 'string' ? duel.result.toLowerCase() : null;
    if (winner) {
      const entry = map.get(winner) || { forcedTurnovers: 0, interceptions: 0 };
      if (result === 'turnover') entry.forcedTurnovers += 1;
      if (duel.type === 'interception') entry.interceptions += 1;
      map.set(winner, entry);
    }
    if (loser) {
      const entry = map.get(loser) || { forcedTurnovers: 0, interceptions: 0 };
      if (result === 'turnover') entry.turnoversConceded = (entry.turnoversConceded || 0) + 1;
      map.set(loser, entry);
    }
  });
  return map;
}

function summarizeRecoveries(match) {
  const boundary = getFinalThirdBoundary(match);
  const rebounds = Array.isArray(match?.rebounds) ? match.rebounds : [];
  const clearanceRecoveries = Array.isArray(match?.clearanceRecoveries) ? match.clearanceRecoveries : [];
  const map = new Map();
  rebounds.forEach((event) => {
    if (!event || typeof event !== 'object') return;
    const name = resolvePlayerName(event.player);
    const team = normalizeTeam(event.team);
    if (!name || !team) return;
    const entry = map.get(name) || { recoveries: 0, finalThirdRecoveries: 0 };
    entry.recoveries += 1;
    if ((team === 'red' && Number(event.x) >= boundary) || (team === 'blue' && Number(event.x) <= -boundary)) {
      entry.finalThirdRecoveries += 1;
    }
    map.set(name, entry);
  });
  clearanceRecoveries.forEach((event) => {
    if (!event || typeof event !== 'object') return;
    const name = resolvePlayerName(event.player);
    if (!name) return;
    const entry = map.get(name) || { recoveries: 0, finalThirdRecoveries: 0 };
    entry.recoveries += 1;
    map.set(name, entry);
  });
  return map;
}

function summarizeKeeperAdvanced(match) {
  const shots = Array.isArray(match?.shots) ? match.shots : [];
  const goals = Array.isArray(match?.goals) ? match.goals : [];
  const gkTicks = summarizeGoalkeeperTicks(match?.gkOccupancy);
  const teamTickTotals = { red: 0, blue: 0 };
  gkTicks.forEach((entry) => {
    const team = normalizeTeam(entry.team);
    if (!team) return;
    teamTickTotals[team] += entry.ticks || 0;
  });
  const keeperAggregate = new Map();
  const teamShots = { red: { xgFaced: 0, shotsFaced: 0, goalsConceded: 0 }, blue: { xgFaced: 0, shotsFaced: 0, goalsConceded: 0 } };
  const goalTicks = new Set(goals.map((goal) => Number(goal?.shot?.tick ?? goal?.gameTicks)).filter((tick) => Number.isFinite(tick)));
  shots.forEach((shot) => {
    if (!shot || typeof shot !== 'object') return;
    const attackingTeam = normalizeTeam(shot.team ?? shot.shotFor ?? shot.for);
    const defendingTeam = attackingTeam === 'red' ? 'blue' : attackingTeam === 'blue' ? 'red' : null;
    if (!defendingTeam) return;
    const matchTick = Number(shot.matchTick ?? shot.replayTick ?? shot.tick);
    const xg = estimateShotXg(shot, { stadium: match?.stadium, redTeam: match?.redTeam, blueTeam: match?.blueTeam });
    const bucket = teamShots[defendingTeam];
    bucket.xgFaced += xg;
    bucket.shotsFaced += 1;
    if (Number.isFinite(matchTick) && goalTicks.has(matchTick)) {
      bucket.goalsConceded += 1;
    }
  });
  gkTicks.forEach((entry, nick) => {
    const team = normalizeTeam(entry.team);
    if (!team) return;
    const share = teamTickTotals[team] > 0 ? (entry.ticks || 0) / teamTickTotals[team] : 0;
    const teamBucket = teamShots[team];
    const stats = {
      xgFaced: teamBucket.xgFaced * share,
      goalsConceded: teamBucket.goalsConceded * share,
      shotsFaced: teamBucket.shotsFaced * share,
      xgPrevented: teamBucket.xgFaced * share - teamBucket.goalsConceded * share,
      sweeperActions: 0
    };
    keeperAggregate.set(nick, stats);
  });
  const kicks = Array.isArray(match?.kicks) ? match.kicks : [];
  const sweeperThreshold = getFinalThirdBoundary(match);
  kicks.forEach((kick) => {
    const name = resolvePlayerName(kick?.player);
    if (!name || !keeperAggregate.has(name)) return;
    if (Math.abs(Number(kick?.x) || 0) > sweeperThreshold) {
      const entry = keeperAggregate.get(name);
      entry.sweeperActions += 1;
    }
  });
  return keeperAggregate;
}

function summarizeGoalChains(match, kicks) {
  const goals = Array.isArray(match?.goals) ? match.goals : [];
  const sortedKicks = [...kicks];
  sortedKicks.sort((a, b) => (Number(a?.tick) || 0) - (Number(b?.tick) || 0));
  const map = new Map();
  goals.forEach((goal) => {
    const goalTick = Number(goal?.shot?.tick ?? goal?.gameTicks);
    const team = normalizeTeam(goal?.for ?? goal?.shot?.team);
    if (!Number.isFinite(goalTick) || !team) return;
    const chain = [];
    for (let i = sortedKicks.length - 1; i >= 0; i -= 1) {
      const kick = sortedKicks[i];
      const tick = Number(kick?.tick);
      const kickTeam = normalizeTeam(kick?.team);
      if (!Number.isFinite(tick) || kickTeam !== team) continue;
      if (tick > goalTick + 5) continue;
      if (goalTick - tick > 720) break;
      const player = resolvePlayerName(kick?.player);
      if (!player) continue;
      if (!chain.length || chain[chain.length - 1] !== player) chain.push(player);
      if (chain.length >= 5) break;
    }
    for (let index = 0; index < chain.length; index += 1) {
      const nick = chain[index];
      const entry = map.get(nick) || { goalChainTouches: 0, secondaryAssists: 0, tertiaryAssists: 0 };
      entry.goalChainTouches += 1;
      if (index === 1) entry.secondaryAssists += 1;
      else if (index === 2) entry.tertiaryAssists += 1;
      map.set(nick, entry);
    }
  });
  return map;
}

function summarizeTransitionMoments(possessions) {
  const map = new Map();
  possessions.forEach((possession, idx) => {
    const next = possessions[idx + 1];
    if (!next) return;
    const teamLost = possession.team;
    const teamWon = next.team;
    if (!teamLost || !teamWon || teamLost === teamWon) return;
    const lastPlayer = possession.lastPlayer;
    if (lastPlayer) {
      const entry = map.get(lastPlayer) || { transitionStops: 0, counterRuns: 0, lastDefenderEvents: 0 };
      entry.lastDefenderEvents += 1;
      map.set(lastPlayer, entry);
    }
    next.players.forEach((info, nick) => {
      if (info.ticks && info.ticks.length) {
        const firstTick = info.ticks[0];
        const delta = firstTick - possession.endTick;
        if (delta <= 180) {
          const entry = map.get(nick) || { transitionStops: 0, counterRuns: 0, lastDefenderEvents: 0 };
          entry.counterRuns += 1;
          map.set(nick, entry);
        }
      }
    });
  });
  return map;
}

export function buildAdvancedPlayerStats(match) {
  const possessions = buildPossessionTimeline(match);
  const possessionStats = summarizePossessionContributions(possessions, match);
  const progressionStats = summarizePassProgressionAdvanced(match?.passes, match);
  const duelPressureStats = summarizeDuelsForPressures(match?.duels);
  const recoveryStats = summarizeRecoveries(match);
  const keeperStats = summarizeKeeperAdvanced(match);
  const kicks = Array.isArray(match?.kicks) ? match.kicks : [];
  const goalChainStats = summarizeGoalChains(match, kicks);
  const transitionStats = summarizeTransitionMoments(possessions);

  const aggregate = new Map();

  const merge = (source, mapper) => {
    if (!source) return;
    source.forEach((value, key) => {
      if (!key) return;
      const entry = aggregate.get(key) || {};
      mapper(entry, value, key);
      aggregate.set(key, entry);
    });
  };

  merge(possessionStats, (entry, value) => {
    entry.possessionTouches = (entry.possessionTouches || 0) + (value.touches || 0);
    entry.pressuredPossessionTouches = (entry.pressuredPossessionTouches || 0) + (value.pressuredTouches || 0);
    entry.possessionsInvolved = (entry.possessionsInvolved || 0) + (value.possessions || 0);
    entry.possessionStarts = (entry.possessionStarts || 0) + (value.possessionStarts || 0);
    entry.scoringPossessionTouches = (entry.scoringPossessionTouches || 0) + (value.scoringTouches || 0);
    entry.turnoverTouches = (entry.turnoverTouches || 0) + (value.turnoverTouches || 0);
    entry.dangerousTurnovers = (entry.dangerousTurnovers || 0) + (value.dangerousTurnovers || 0);
    entry.turnoversCommitted = (entry.turnoversCommitted || 0) + (value.turnoversCommitted || 0);
    entry.forcedTurnovers = (entry.forcedTurnovers || 0) + (value.forcedTurnovers || 0);
  });

  merge(progressionStats, (entry, value) => {
    entry.progressivePasses = (entry.progressivePasses || 0) + (value.progressivePasses || 0);
    entry.progressiveDistance = (entry.progressiveDistance || 0) + (value.progressiveDistance || 0);
    entry.finalThirdEntries = (entry.finalThirdEntries || 0) + (value.finalThirdEntries || 0);
    entry.keyPasses = (entry.keyPasses || 0) + (value.keyPasses || 0);
    entry.forwardPasses = (entry.forwardPasses || 0) + (value.forwardPasses || 0);
    entry.backwardPasses = (entry.backwardPasses || 0) + (value.backwardPasses || 0);
    entry.lateralPasses = (entry.lateralPasses || 0) + (value.lateralPasses || 0);
  });

  merge(duelPressureStats, (entry, value) => {
    entry.interceptions = (entry.interceptions || 0) + (value.interceptions || 0);
    if (value.turnoversConceded) {
      entry.turnoversCommitted = (entry.turnoversCommitted || 0) + value.turnoversConceded;
    }
  });

  merge(recoveryStats, (entry, value) => {
    entry.recoveries = (entry.recoveries || 0) + (value.recoveries || 0);
    entry.finalThirdRecoveries = (entry.finalThirdRecoveries || 0) + (value.finalThirdRecoveries || 0);
  });

  merge(keeperStats, (entry, value) => {
    entry.xgFaced = (entry.xgFaced || 0) + (value.xgFaced || 0);
    entry.goalsConceded = (entry.goalsConceded || 0) + (value.goalsConceded || 0);
    entry.shotsFaced = (entry.shotsFaced || 0) + (value.shotsFaced || 0);
    entry.xgPrevented = (entry.xgPrevented || 0) + (value.xgPrevented || 0);
    entry.sweeperActions = (entry.sweeperActions || 0) + (value.sweeperActions || 0);
  });

  merge(goalChainStats, (entry, value) => {
    entry.goalChainTouches = (entry.goalChainTouches || 0) + (value.goalChainTouches || 0);
    entry.secondaryAssists = (entry.secondaryAssists || 0) + (value.secondaryAssists || 0);
    entry.tertiaryAssists = (entry.tertiaryAssists || 0) + (value.tertiaryAssists || 0);
  });

  merge(transitionStats, (entry, value) => {
    entry.counterRuns = (entry.counterRuns || 0) + (value.counterRuns || 0);
    entry.lastDefenderEvents = (entry.lastDefenderEvents || 0) + (value.lastDefenderEvents || 0);
  });

  return {
    possessions,
    advanced: aggregate
  };
}
